package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.AdminOcrAnalysisStrings
import com.milta.core.localization.LocalStrings
import com.milta.feature.admin.domain.entity.AdminOcrAnalysisModeSummary
import com.milta.feature.admin.domain.usecase.GetAdminOcrAnalysisUseCase
import com.milta.feature.admin.presentation.AdminOcrAnalysisState
import com.milta.feature.admin.presentation.AdminOcrAnalysisViewModel
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

/** Column widths shared by the comparison table's header bar and every mode row. */
private const val OcrAnalysisGridTemplate = "1.3fr 1fr 1.1fr 1.1fr 1.1fr 1.3fr"

/**
 * Admin "Usage IA" screen (2026-10-05) — read-only cost-comparison view over the background OCR
 * analysis pipeline: what was actually spent on real Claude calls vs. what each of the 4 simulated
 * PipelineModes would have cost instead. See GetAdminOcrAnalysisQuery (milta backend repo).
 */
@Composable
fun AdminOcrAnalysisContent() {
    val getAdminOcrAnalysis: GetAdminOcrAnalysisUseCase = koinInject()
    val viewModel = remember { AdminOcrAnalysisViewModel(getAdminOcrAnalysis) }
    val state by viewModel.state.collectAsState()

    val strings = LocalStrings.current.adminOcrAnalysis

    when {
        state.isLoading -> Div(attrs = { style { padding(40.px); property("display", "flex"); property("align-items", "center") } }) {
            WebSpinner()
            Div(attrs = { style { padding(0.px, 0.px, 0.px, 12.px); property("color", WebTextSecondary); fontSize(13.5.px) } }) { Text(strings.loading) }
        }
        state.error != null -> Div(
            attrs = { style { padding(16.px); property("background", WebErrorBg); property("color", WebErrorInk); property("border-radius", "10px") } },
        ) { Text(if (state.error == "offline") strings.errorNoConnection else strings.errorGeneric) }
        else -> AdminOcrAnalysisBody(state = state, strings = strings)
    }
}

@Composable
private fun AdminOcrAnalysisBody(state: AdminOcrAnalysisState, strings: AdminOcrAnalysisStrings) {
    val analysis = state.analysis ?: return

    if (analysis.summary.totalDocuments == 0) {
        Div(attrs = { style { padding(36.px, 16.px); property("text-align", "center"); property("color", WebTextSecondary); fontSize(13.px) } }) {
            Text(strings.emptyStateMessage)
        }
        return
    }

    val byFileKind = analysis.summary.documentsByFileKind
    val breakdown = listOf(
        strings.fileKindImageLabel to (byFileKind["Image"] ?: 0),
        strings.fileKindPdfLabel to (byFileKind["Pdf"] ?: 0),
        strings.fileKindXlsxLabel to (byFileKind["Xlsx"] ?: 0),
    ).joinToString(" · ") { (label, count) -> "$label: $count" }

    Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "repeat(5, 1fr)"); property("gap", "12px"); property("margin-bottom", "20px") } }) {
        Kpi(strings.kpiTotalDocumentsLabel, analysis.summary.totalDocuments.toString(), "${strings.breakdownByFileKindLabel} — $breakdown")
        Kpi(strings.kpiCompletedDocumentsLabel, analysis.summary.completedDocuments.toString(), strings.kpiCompletedDocumentsSubtitle)
        Kpi(strings.kpiTotalRealCostLabel, formatUsd(analysis.summary.totalRealCost), "")
        Kpi(strings.kpiTotalInputTokensLabel, analysis.summary.totalInputTokens.toString(), "")
        Kpi(strings.kpiTotalOutputTokensLabel, analysis.summary.totalOutputTokens.toString(), "")
    }

    Div(attrs = { style { fontSize(13.5.px); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
        Text(strings.tableTitle)
    }

    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "10px")
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("margin-bottom", "14px")
        }
    }) {
        OcrAnalysisGridRow(padding = "10px 16px") {
            OcrAnalysisHeaderCell(strings.columnMode)
            OcrAnalysisHeaderCell(strings.columnDocuments)
            OcrAnalysisHeaderCell(strings.columnClaudeCalls)
            OcrAnalysisHeaderCell(strings.columnProjectedCost)
            OcrAnalysisHeaderCell(strings.columnRealCost)
            OcrAnalysisHeaderCell(strings.columnSavings, alignRight = true)
        }
    }

    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "14px")
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("overflow", "hidden")
        }
    }) {
        analysis.modeSummaries.forEachIndexed { index, mode ->
            OcrAnalysisModeRow(mode = mode, strings = strings, isLast = index == analysis.modeSummaries.lastIndex)
        }
    }

    OcrPipelineDiagram(strings)
}

/**
 * Explains the real, complete production flow — rendered as an actual Mermaid.js flowchart (see
 * MermaidRenderer.kt), not a hand-rolled CSS-box approximation. Deliberately a FAITHFUL
 * reproduction of what this port actually does today (OcrAnalysisBackgroundService): duplicate
 * documents skip redundant local analysis (never the real Claude call/cost), a scanned PDF is
 * rasterized page-by-page and OCR'd locally like a photo, and spreadsheets get a local structural
 * read — all 3 were explicit gaps vs. the original milta-ocr-cost-poc design, now closed.
 */
@Composable
private fun OcrPipelineDiagram(strings: AdminOcrAnalysisStrings) {
    Div(attrs = { style { fontSize(13.5.px); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
        Text(strings.flowTitle)
    }
    MermaidDiagram(buildMermaidDefinition(strings))
}

@Composable
private fun MermaidDiagram(definition: String) {
    var svg by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(definition) {
        svg = try {
            renderMermaidToSvg(definition, "usage-ia-flow")
        } catch (error: Throwable) {
            console.error("Mermaid render failed", error)
            null
        }
    }

    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px")
                padding(16.px); property("margin-bottom", "22px"); property("overflow-x", "auto")
            }
        },
    ) {
        DisposableRefEffect(svg) { element ->
            element.innerHTML = svg ?: ""
            onDispose { }
        }
    }
}

/** Builds the Mermaid flowchart definition text, reusing the SAME translated strings shown
 * elsewhere on this screen — every node/edge label comes from [AdminOcrAnalysisStrings], nothing
 * hardcoded in French here. Every label is double-quoted (mermaidLabel) since several contain
 * punctuation that would otherwise be ambiguous with Mermaid's own flowchart syntax. */
private fun buildMermaidDefinition(strings: AdminOcrAnalysisStrings): String {
    fun node(id: String, title: String, subtitle: String? = null) =
        "$id[${mermaidLabel(if (subtitle != null) "$title — $subtitle" else title)}]"

    fun decision(id: String, title: String) = "$id{${mermaidLabel(title)}}"

    fun edge(from: String, to: String, label: String? = null) =
        if (label != null) "    $from -- ${mermaidLabel(label)} --> $to" else "    $from --> $to"

    return buildString {
        appendLine("flowchart TD")
        appendLine("    ${node("A", strings.flowStep1Title, strings.flowStep1Subtitle)}")
        appendLine(edge("A", decision("B", strings.flowStep2Title)))
        appendLine(edge("B", node("B1", strings.flowStep2Note), strings.flowYesLabel))
        appendLine(edge("B", decision("C", strings.flowStep3Title), strings.flowNoLabel))
        appendLine(edge("B1", "C"))
        appendLine()
        appendLine(edge("C", node("D1", strings.flowImageStep1Title, strings.flowImageStep1Subtitle), strings.flowImageBranchTitle))
        appendLine(edge("D1", decision("D2", strings.flowImageStep2Title)))
        appendLine(edge("D2", node("D2b", strings.flowImageStep2Subtitle), strings.flowNoLabel))
        appendLine(edge("D2", node("D3", strings.flowImageStep3Title, strings.flowImageStep3Subtitle), strings.flowYesLabel))
        appendLine(edge("D2b", "D3"))
        appendLine(edge("D3", node("D4", strings.flowImageStep4Title, strings.flowImageStep4Subtitle)))
        appendLine(edge("D4", node("D5", strings.flowImageStep5Title, strings.flowImageStep5Subtitle)))
        appendLine()
        appendLine(edge("C", decision("E1", strings.flowPdfStep1Title), strings.flowPdfBranchTitle))
        appendLine(edge("E1", node("E2", strings.flowPdfStep1Subtitle), strings.flowYesLabel))
        appendLine(edge("E1", node("E3", strings.flowPdfNote), strings.flowNoLabel))
        appendLine()
        appendLine(edge("C", node("F1", strings.flowXlsxStep1Title, strings.flowXlsxStep1Subtitle), strings.flowXlsxBranchTitle))
        appendLine(edge("F1", node("F2", strings.flowXlsxNote)))
        appendLine()
        appendLine(edge("D5", node("G", strings.flowClaudeTitle, strings.flowClaudeSubtitle)))
        appendLine(edge("E2", "G"))
        appendLine(edge("E3", "G"))
        appendLine(edge("F2", "G"))
        appendLine()
        appendLine(edge("G", node("H", strings.flowPersistTitle, strings.flowPersistSubtitle)))
        appendLine(edge("H", node("I", strings.flowSimulationTitle)))
        appendLine(edge("I", node("J", strings.flowDashboardTitle, strings.flowDashboardSubtitle)))
    }
}

private fun mermaidLabel(text: String): String =
    "\"${text.replace("\"", "'").replace("\n", " ")}\""

@Composable
private fun OcrAnalysisModeRow(mode: AdminOcrAnalysisModeSummary, strings: AdminOcrAnalysisStrings, isLast: Boolean) {
    Div(attrs = { style { if (!isLast) property("border-bottom", "1px solid $WebBorder") } }) {
        OcrAnalysisGridRow(padding = "11px 16px") {
            Div {
                Div(attrs = { style { fontSize(13.px); property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(modeLabel(mode.mode, strings)) }
                Div(attrs = { style { fontSize(11.px); property("color", WebTextSecondary); property("line-height", "1.4"); property("margin-top", "2px") } }) { Text(modeDescription(mode.mode, strings)) }
            }
            OcrAnalysisCell(mode.documentCount.toString())
            OcrAnalysisCell(mode.claudeCallCount.toString())
            OcrAnalysisCell(formatUsd(mode.totalProjectedCost))
            OcrAnalysisCell(formatUsd(mode.totalRealCostForModeSet))
            val savingsColor = when {
                mode.projectedSavings > 0 -> WebSuccessInk
                mode.projectedSavings < 0 -> WebErrorInk
                else -> WebTextSecondary
            }
            Div(attrs = { style { fontSize(12.5.px); property("font-weight", "700"); property("color", savingsColor); property("text-align", "right") } }) {
                Text(formatUsd(mode.projectedSavings))
            }
        }
    }
}

private fun modeLabel(mode: String, strings: AdminOcrAnalysisStrings) = when (mode) {
    "Baseline" -> strings.modeBaseline
    "Minimal" -> strings.modeMinimal
    "TesseractGate" -> strings.modeTesseractGate
    "TesseractOnly" -> strings.modeTesseractOnly
    else -> mode
}

private fun modeDescription(mode: String, strings: AdminOcrAnalysisStrings) = when (mode) {
    "Baseline" -> strings.modeBaselineDescription
    "Minimal" -> strings.modeMinimalDescription
    "TesseractGate" -> strings.modeTesseractGateDescription
    "TesseractOnly" -> strings.modeTesseractOnlyDescription
    else -> ""
}

@Composable
private fun OcrAnalysisGridRow(padding: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("display", "grid"); property("grid-template-columns", OcrAnalysisGridTemplate)
            property("align-items", "center"); property("gap", "8px"); property("padding", padding)
        }
    }) { content() }
}

@Composable
private fun OcrAnalysisHeaderCell(label: String, alignRight: Boolean = false) {
    Div(attrs = { style { fontSize(10.5.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); if (alignRight) property("text-align", "right") } }) {
        Text(label)
    }
}

@Composable
private fun OcrAnalysisCell(text: String) {
    Div(attrs = { style { fontSize(12.5.px); property("color", WebTextSecondary) } }) { Text(text) }
}

/** No String.format (JVM-only, forbidden in this codebase's commonMain/jsMain — see
 * pitfall_wasmjs_jsfun_package_split) — rounds to 4 decimal places via plain arithmetic since OCR
 * per-document costs are small (fractions of a cent). */
private fun formatUsd(amount: Double): String {
    val rounded = kotlin.math.round(amount * 10000) / 10000.0
    return "$$rounded"
}
